import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface Fornecedor {
  id: number;
  nome: string;
  cnpj?: string;
  telefone?: string;
  email?: string;
  endereco?: string;
  observacoes?: string;
  ativo: boolean;
  created_at: string;
  updated_at: string;
}

export function useFornecedores(apenasAtivos = false) {
  return useQuery({
    queryKey: ['fornecedores', apenasAtivos],
    queryFn: async () => {
      let query = supabase
        .from('fornecedores')
        .select('*')
        .order('nome', { ascending: true });
      if (apenasAtivos) query = query.eq('ativo', true);
      const { data, error } = await query;
      if (error) {
        console.error('Erro ao buscar fornecedores:', error);
        throw error;
      }
      return (data || []) as Fornecedor[];
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateFornecedor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (fornecedor: {
      nome: string;
      cnpj?: string;
      telefone?: string;
      email?: string;
      endereco?: string;
      observacoes?: string;
      ativo?: boolean;
    }) => {
      const { data, error } = await supabase
        .from('fornecedores')
        .insert({ ...fornecedor, ativo: fornecedor.ativo ?? true })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fornecedores'] });
      toast.success('Fornecedor cadastrado com sucesso!');
    },
    onError: () => {
      toast.error('Erro ao cadastrar fornecedor');
    },
  });
}

export function useEditarFornecedor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (fornecedor: {
      id: number;
      nome: string;
      cnpj?: string;
      telefone?: string;
      email?: string;
      endereco?: string;
      observacoes?: string;
      ativo: boolean;
    }) => {
      const { id, ...rest } = fornecedor;
      const { data, error } = await supabase
        .from('fornecedores')
        .update({ ...rest, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fornecedores'] });
      toast.success('Fornecedor editado com sucesso!');
    },
    onError: () => {
      toast.error('Erro ao editar fornecedor');
    },
  });
}

export function useExcluirFornecedor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const { error } = await supabase
        .from('fornecedores')
        .update({ ativo: false, updated_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw error;
      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fornecedores'] });
      toast.success('Fornecedor excluído com sucesso!');
    },
    onError: () => {
      toast.error('Erro ao excluir fornecedor');
    },
  });
}
